import React from 'react';
import { Link } from 'react-router-dom';
import {
  CheckCircleIcon,
  CloudArrowUpIcon,
  CpuChipIcon,
  DocumentMagnifyingGlassIcon,
  WrenchScrewdriverIcon,
  BoltIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';
import { SparklesIcon } from '@heroicons/react/24/solid';
import SEO from '../components/SEO';
import { useAppContext } from '../context/AppContext';
import { HomePageHeader } from './HomePageNew';
import { trackGoal } from '../utils/metrika';

const pageJsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Распознавание однолинейных электрических схем из PDF",
    "url": "https://get-goods.ai/raspoznavanie-odnolineynyh-shem",
    "description": "Get-Goods.ai распознает однолинейные электрические схемы в PDF проектной документации и подбирает электротехнические изделия по извлеченной спецификации."
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "Какие PDF-документы можно загрузить?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Можно загрузить PDF с проектной или рабочей документацией по электрике, включая разделы с однолинейными схемами, таблицами нагрузок, щитами и спецификациями."
        }
      },
      {
        "@type": "Question",
        "name": "Что Get-Goods.ai извлекает из однолинейной схемы?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Система извлекает обозначения оборудования, номиналы автоматов, кабели, щиты, линии, нагрузки и связанные технические характеристики для формирования спецификации."
        }
      },
      {
        "@type": "Question",
        "name": "Можно ли подобрать товары после распознавания?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Да. После распознавания Get-Goods.ai сопоставляет позиции спецификации с каталогом электротехнических изделий и предлагает подходящие варианты подбора."
        }
      }
    ]
  }
];

const recognitionSteps = [
  {
    icon: CloudArrowUpIcon,
    title: "Загрузка PDF",
    text: "Пользователь загружает раздел проектной или рабочей документации по электрике."
  },
  {
    icon: DocumentMagnifyingGlassIcon,
    title: "Поиск схем",
    text: "ИИ определяет страницы с однолинейными схемами, щитами и таблицами нагрузок."
  },
  {
    icon: CpuChipIcon,
    title: "Распознавание",
    text: "Система извлекает обозначения, номиналы, кабели, нагрузки и состав оборудования."
  },
  {
    icon: CheckCircleIcon,
    title: "Подбор",
    text: "Распознанная спецификация используется для поиска подходящих электротехнических изделий."
  }
];

const extractedItems = [
  "автоматические выключатели, УЗО, дифавтоматы и вводные аппараты",
  "щиты, распределительные панели, отходящие линии и групповые цепи",
  "марки кабелей, сечения, длины, нагрузки и расчетные параметры",
  "позиционные обозначения, комментарии, таблицы нагрузок и спецификации"
];

const audienceItems = [
  {
    title: "Проектировщикам",
    text: "быстрее проверять состав раздела и получать перечень оборудования из схем."
  },
  {
    title: "Снабжению",
    text: "переходить от PDF-документации к списку позиций для запроса цен и аналогов."
  },
  {
    title: "Подрядчикам",
    text: "разбирать раздел ПД/РД без ручного переписывания схем и таблиц."
  }
];

const SingleLineSchemesPage: React.FC = () => {
  const { isAuthenticated } = useAppContext();
  const targetPath = isAuthenticated ? "/my-applications" : "/login";
  const trackSeoClick = (source: string) => {
    trackGoal('seo_page_click', {
      source,
      seo_path: '/raspoznavanie-odnolineynyh-shem',
      destination: targetPath,
      authenticated: isAuthenticated,
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Распознавание однолинейных электрических схем из PDF - Get-Goods.ai"
        description="Get-Goods.ai распознает однолинейные электрические схемы в PDF-разделах проектной документации и подбирает электротехнические изделия по извлеченной спецификации."
        canonicalPath="/raspoznavanie-odnolineynyh-shem"
        imagePath="/assets/scheme_example.png"
        keywords="распознавание однолинейных схем, однолинейная схема PDF, раздел ПД электрика, распознавание проектной документации, подбор электротехнического оборудования"
        jsonLd={pageJsonLd}
      />
      <HomePageHeader />

      <main className="pt-20">
        <section className="bg-gray-900 text-white">
          <div className="container mx-auto px-6 py-16 lg:py-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <p className="text-sm font-semibold uppercase tracking-wide text-blue-300 mb-4">
                  PDF проектной документации по электрике
                </p>
                <h1 className="text-4xl md:text-6xl font-extrabold leading-tight mb-6">
                  Распознавание однолинейных электрических схем
                </h1>
                <p className="text-xl text-gray-300 leading-relaxed mb-8">
                  Get-Goods.ai находит однолинейные схемы внутри PDF-раздела ПД/РД,
                  извлекает оборудование и формирует спецификацию, по которой можно
                  сразу подобрать электротехнические изделия.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to={targetPath}
                    onClick={() => trackSeoClick('schemes_hero')}
                    className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-500 text-white px-7 py-4 rounded-lg text-base font-semibold transition-colors duration-300 shadow-lg"
                  >
                    {isAuthenticated ? (
                      <CloudArrowUpIcon className="h-6 w-6 mr-2" />
                    ) : (
                      <SparklesIcon className="h-6 w-6 mr-2" />
                    )}
                    {isAuthenticated ? "Загрузить PDF" : "Начать распознавание"}
                  </Link>
                  <a
                    href="#how-it-works"
                    className="inline-flex items-center justify-center border border-gray-500 hover:border-blue-300 text-white px-7 py-4 rounded-lg text-base font-semibold transition-colors duration-300"
                  >
                    Как работает
                  </a>
                </div>
              </div>

              <div className="bg-white p-3 rounded-lg shadow-2xl">
                <img
                  src="/assets/scheme_example.png"
                  alt="Однолинейная электрическая схема PDF с распознанными областями"
                  className="w-full rounded-md border border-gray-200"
                />
              </div>
            </div>
          </div>
        </section>

        <section id="how-it-works" className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mb-12">
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-4">
                От раздела ПД к готовой спецификации
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Сценарий рассчитан на документы, где важные позиции находятся не только
                в таблице, но и на самой однолинейной схеме: рядом с линиями, щитами,
                защитными аппаратами и нагрузками.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {recognitionSteps.map((step) => (
                <div key={step.title} className="border border-gray-200 rounded-lg p-6 bg-gray-50">
                  <step.icon className="h-10 w-10 text-blue-600 mb-4" />
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-20 bg-gray-50">
          <div className="container mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <div className="flex items-center gap-3 mb-5">
                  <BoltIcon className="h-10 w-10 text-amber-500" />
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                    Что распознается
                  </h2>
                </div>
                <p className="text-lg text-gray-600 leading-relaxed mb-6">
                  Алгоритм ориентирован на электротехническую документацию и извлекает
                  данные, которые нужны для подбора оборудования и последующей закупки.
                </p>
                <div className="space-y-4">
                  {extractedItems.map((item) => (
                    <div key={item} className="flex items-start gap-3">
                      <CheckCircleIcon className="h-6 w-6 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <div className="flex items-center gap-3 mb-5">
                  <WrenchScrewdriverIcon className="h-10 w-10 text-blue-600" />
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                    Кому полезно
                  </h2>
                </div>
                <div className="space-y-5">
                  {audienceItems.map((item) => (
                    <div key={item.title} className="bg-white border border-gray-200 rounded-lg p-6">
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{item.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-20 bg-white">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto text-center">
              <ChartBarIcon className="h-12 w-12 text-blue-600 mx-auto mb-5" />
              <h2 className="text-3xl md:text-5xl font-bold text-gray-900 mb-5">
                Подбор по распознанной спецификации
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-8">
                После распознавания Get-Goods.ai нормализует технические характеристики
                и использует их для поиска подходящих электротехнических изделий,
                аналогов и вариантов закупки.
              </p>
              <Link
                to={targetPath}
                onClick={() => trackSeoClick('schemes_bottom_cta')}
                className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg text-base font-semibold transition-colors duration-300 shadow-lg"
              >
                {isAuthenticated ? "Загрузить документацию" : "Попробовать Get-Goods.ai"}
              </Link>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default SingleLineSchemesPage;
